'use client';

import React, { memo, useCallback, useRef, useState } from 'react';

import { ChevronTightRight } from '@repo/ui/components/icons/chevron-tight-right';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/ui/tooltip';

interface ArticleCardSummaryProps {
  summary?: string;
  aiSummary?: string;
}

const PureArticleCardSummary: React.FC<ArticleCardSummaryProps> = ({
  summary,
  aiSummary,
}) => {
  const [isSummaryTooltipOpen, setIsSummaryTooltipOpen] = useState(false);

  const summaryTooltipRef = useRef<HTMLParagraphElement>(null);

  const handleSummaryTooltipOpenChange = useCallback(
    (open: boolean) => {
      if (summaryTooltipRef.current) {
        const isClamped =
          summaryTooltipRef.current.scrollHeight >
          summaryTooltipRef.current.clientHeight;
        setIsSummaryTooltipOpen(open && isClamped);
      }
    },
    [setIsSummaryTooltipOpen],
  );

  return (
    <div className="flex w-full items-center justify-between gap-2">
      <TooltipProvider>
        <Tooltip
          delayDuration={200}
          open={isSummaryTooltipOpen}
          onOpenChange={handleSummaryTooltipOpenChange}
        >
          <TooltipTrigger asChild>
            <p
              ref={summaryTooltipRef}
              className="line-clamp-7 whitespace-break-spaces text-left font-satoshi text-xs font-semibold leading-3.5 text-[#292929] cred-md:text-sm cred-md:leading-3.5 cred-lg:text-sm cred-lg:leading-[15.4px] cred-xl:text-lg cred-xl:leading-5 wrap-break-word"
            >
              {summary || aiSummary}
            </p>
          </TooltipTrigger>
          <TooltipContent className="max-w-72 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.3)] cred-md:max-w-80 cred-lg:max-w-[330px] cred-xl:max-w-[440px]">
            <p className="text-xs text-[#292929] cred-md:text-sm cred-lg:text-sm cred-xl:text-lg md:text-base md:leading-[19px] wrap-break-word">
              {summary || aiSummary}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <ChevronTightRight className="h-10 w-4 shrink-0 text-neutral-2" />
    </div>
  );
};
export const ArticleCardSummary = memo(
  PureArticleCardSummary,
  (prevProps, nextProps) => {
    return (
      prevProps.summary === nextProps.summary &&
      prevProps.aiSummary === nextProps.aiSummary
    );
  },
);
